import React from 'react'

import { Navigate, useLocation, useParams, type RouteObject } from 'react-router-dom'

import Layout from '@/components/Layout/Layout'
import { useAuth } from '@/hooks'
import AdminPage from '@/pages/admin/AdminPage'
import AdminUserDetailPage from '@/pages/admin/AdminUserDetailPage'
import AdminUsersPage from '@/pages/admin/AdminUsersPage'
import AdminAssetGroupDetailPage from '@/pages/admin/asset-groups/AdminAssetGroupDetailPage'
import AdminAssetGroupsPage from '@/pages/admin/asset-groups/AdminAssetGroupsPage'
import AdminBulkPermissionsPage from '@/pages/admin/bulk-permissions/AdminBulkPermissionsPage'
import AdminCompaniesBulkPage from '@/pages/admin/companies/AdminCompaniesBulkPage'
import AdminCompaniesPage from '@/pages/admin/companies/AdminCompaniesPage'
import AdminCompanyDetailPage from '@/pages/admin/companies/AdminCompanyDetailPage'
import MigrationComparePage from '@/pages/admin/migration/MigrationComparePage'
import MigrationDashboardPage from '@/pages/admin/migration/MigrationDashboardPage'
import MigrationItemPage from '@/pages/admin/migration/MigrationItemPage'
import AdminNewUserPage from '@/pages/admin/new-user/AdminNewUserPage'
import NewShipmentPage from '@/pages/admin/shipments/NewShipmentPage'
import AdminUserImportPage from '@/pages/admin/user-import/AdminUserImportPage'
import AlertAcknowledgementPage from '@/pages/AlertAcknowledgementPage/AlertAcknowledgementPage'
import AssetMapPage from '@/pages/assets/map'
import AuditLogsPage from '@/pages/audit-logs'
import ColdStoragePage, { BlastFreezerPage } from '@/pages/cold-storage'
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout'
import DashboardCreate from '@/pages/dashboard/Create'
import DashboardSettings from '@/pages/dashboard/Settings'
import DashboardView from '@/pages/dashboard/View'
import ErrorPage from '@/pages/ErrorPage'
import { GeofenceCategoriesPage, GeofenceEditorPage, GeofencesPage } from '@/pages/geofences'
import HelpPage from '@/pages/Help/HelpPage'
import NoAppsPage from '@/pages/NoAppsPage'
import NotFoundPage from '@/pages/NotFoundPage'
import TrackerMapPage from '@/pages/trackers/map'
import { firstAppPath } from '@/utils/appHome'

// Home is the first app the user holds (MEGA-779) — the Asset List for most. The
// user's default dashboard is still configurable but doesn't drive the initial page.
const RootRedirect: React.FC = () => {
  const { hasPermission } = useAuth()
  return <Navigate to={firstAppPath(hasPermission)} replace />
}

/** Sends the retired `/trackers/audit-logs[/:auditId]` URLs to `/audit-logs`, filters intact. */
const LegacyAuditLogRedirect: React.FC = () => {
  const { auditId } = useParams()
  const { search } = useLocation()
  return <Navigate to={{ pathname: auditId ? `/audit-logs/${auditId}` : '/audit-logs', search }} replace />
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      // Root redirect to the asset map
      {
        index: true,
        element: <RootRedirect />,
      },
      // MyGeotab add-in entry point. The add-in shim frames this path; isGeotabMode()
      // latches onto it (see services/geotabSession) so add-in mode survives the redirect.
      {
        path: 'geotab',
        element: <RootRedirect />,
      },
      // Public routes
      {
        path: 'help',
        element: <HelpPage />,
      },
      // Routes (auth enforced by AuthProvider before children render)
      {
        path: 'assets',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <AssetMapPage />,
          },
          {
            path: ':entityId',
            children: [
              {
                index: true,
                element: <Navigate to="default" replace />,
              },
              {
                path: 'details',
                element: <div>if we have a default asset detail page</div>,
              },
              {
                path: ':slug',
                element: <DashboardView />,
              },
            ],
          },
        ],
      },
      {
        path: 'cold-storage',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <ColdStoragePage />,
          },
        ],
      },
      {
        path: 'blast-freezer',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <BlastFreezerPage />,
          },
        ],
      },
      {
        path: 'trackers',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <TrackerMapPage />,
          },
          // Audit logs used to live here; keep old links working.
          {
            path: 'audit-logs',
            element: <LegacyAuditLogRedirect />,
          },
          {
            path: 'audit-logs/:auditId',
            element: <LegacyAuditLogRedirect />,
          },
        ],
      },
      // Where a user with no apps lands (see utils/appHome).
      {
        path: 'no-apps',
        element: <DashboardLayout />,
        children: [{ index: true, element: <NoAppsPage /> }],
      },
      // Audit logs are not tracker-specific (alert configs, subscriptions, user admin),
      // so they sit at the top level rather than under /trackers.
      {
        path: 'audit-logs',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <AuditLogsPage />,
          },
          {
            path: ':auditId',
            element: <AuditLogsPage />,
          },
        ],
      },
      // Geofence management (Refresh's Geofences and Geofence Categories screens), on the
      // `geofences` grant. The open geofence lives in the route so it is deep-linkable.
      {
        path: 'geofences',
        element: <DashboardLayout />,
        children: [
          { index: true, element: <GeofencesPage /> },
          // Static segment ahead of the :geofenceId routes so the order is obvious to a reader.
          { path: 'new', element: <GeofenceEditorPage /> },
          { path: ':geofenceId', element: <GeofencesPage /> },
          { path: ':geofenceId/edit', element: <GeofenceEditorPage /> },
        ],
      },
      {
        path: 'geofence-categories',
        element: <DashboardLayout />,
        children: [{ index: true, element: <GeofenceCategoriesPage /> }],
      },
      // Staff-only admin area. The nav entry is gated on the `staff` permission and every
      // endpoint behind these pages enforces it server-side too.
      {
        path: 'admin',
        element: <DashboardLayout />,
        children: [
          { index: true, element: <AdminPage /> },
          { path: 'users', element: <AdminUsersPage /> },
          { path: 'users/new', element: <AdminNewUserPage /> },
          // Static segment ahead of the :uuid route so the order is obvious to a reader.
          { path: 'users/permissions', element: <AdminBulkPermissionsPage /> },
          { path: 'users/import', element: <AdminUserImportPage /> },
          { path: 'users/:uuid', element: <AdminUserDetailPage /> },
          { path: 'companies', element: <AdminCompaniesPage /> },
          { path: 'companies/permissions', element: <AdminCompaniesBulkPage /> },
          { path: 'companies/:customerId', element: <AdminCompanyDetailPage /> },
          { path: 'asset-groups', element: <AdminAssetGroupsPage /> },
          { path: 'asset-groups/:companyId', element: <AdminAssetGroupDetailPage /> },
          { path: 'migration', element: <MigrationDashboardPage /> },
          { path: 'migration/:id', element: <MigrationItemPage /> },
          { path: 'migration/:id/compare', element: <MigrationComparePage /> },
          { path: 'shipments', element: <Navigate to="new" replace /> },
          { path: 'shipments/new', element: <NewShipmentPage /> },
        ],
      },
      {
        path: 'dashboards',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="default" replace />,
          },
          {
            path: 'new',
            element: <DashboardCreate />,
          },
          {
            path: 'settings',
            element: <DashboardSettings />,
          },
          {
            path: ':slug/:detailId',
            element: <DashboardView />,
          },
          {
            path: ':slug',
            element: <DashboardView />,
          },
        ],
      },
      {
        path: 'alert-acknowledgement',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <AlertAcknowledgementPage />,
          },
          {
            path: '*',
            element: <Navigate to="/dashboards/default" replace />,
          },
        ],
      },
      // Catch-all 404
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]
